import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { useArnoOverview } from '../../hooks/reactQuerys/ArnoAdminQueries';
import { modelStateMessage, openMappingCount } from './arnoAdminLabels';
import { DecisionTemplateTab } from './DecisionTemplateTab';
import { MappingTab } from './MappingTab';
import { UnresolvedReferenceTab } from './UnresolvedReferenceTab';
import { ImportRunTab } from './ImportRunTab';

/**
 * Verwaltung der Entscheidungsbäume (#1245).
 *
 * Ein Entscheidungsbaum ist eine wiederverwendbare Logik zur Urkundenerzeugung.
 * Er kommt aus einem ArnoTop-Import oder wird in DiNo angelegt und wird beliebig
 * vielen DiNo-Rechtsgeschäftsarten freigegeben — ArnoTop ist eine Importquelle,
 * nicht das Modell.
 *
 * Vier Arbeiten, in der Reihenfolge, in der sie anfallen: Bäume freigeben,
 * Legacy-Variablen zuordnen, offene Bausteinverweise klären, Importläufe
 * ansehen. Die letzten drei betreffen nur importierte Bäume. Der Reiter steht in
 * der Adresse, damit ein Link auf eine bestimmte Liste zeigen kann.
 */

const VIEWS = [
  'entscheidungsbaeume',
  'zuordnungen',
  'verweise',
  'importe',
] as const;

type View = (typeof VIEWS)[number];

const TAB_LABELS: Record<View, string> = {
  entscheidungsbaeume: 'Entscheidungsbäume',
  zuordnungen: 'Zuordnungen',
  verweise: 'Offene Verweise',
  importe: 'Importe',
};

export const ArnoTopAdminView: React.FC = () => {
  const { currentView } = useParams();
  const navigate = useNavigate();
  const {
    arnoOverview,
    isLoadingArnoOverview,
    isFetchingArnoOverview,
    refetchArnoOverview,
  } = useArnoOverview();

  const view: View = VIEWS.includes(currentView as View)
    ? (currentView as View)
    : 'entscheidungsbaeume';
  const state = modelStateMessage(arnoOverview);

  const figures: { label: string; value: string; hint?: string }[] = [
    {
      label: 'Bäume freigegeben',
      value: arnoOverview
        ? `${arnoOverview.assignedTemplates} / ${arnoOverview.templates + arnoOverview.nativeTemplates}`
        : '–',
      hint: arnoOverview
        ? `${arnoOverview.assignments} Freigaben für ${arnoOverview.assignedTypes} Rechtsgeschäftsarten`
        : undefined,
    },
    {
      label: 'Zuordnungen offen',
      value: arnoOverview ? String(openMappingCount(arnoOverview)) : '–',
    },
    {
      label: 'Von Hand entschieden',
      value: arnoOverview ? String(arnoOverview.humanMappings) : '–',
    },
    {
      label: 'Verweise ohne Ziel',
      value: arnoOverview ? String(arnoOverview.unresolvedReferenceKeys) : '–',
      hint: arnoOverview
        ? `an ${arnoOverview.unresolvedReferences} Stellen im Bestand`
        : undefined,
    },
  ];

  return (
    <Box sx={{ p: 1 }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant='h5'>Dokumentvorlagen</Typography>
          <Typography variant='body2' color='text.secondary'>
            Entscheidungsbäume für die Urkundenerzeugung und ihre Freigabe für
            DiNo-Rechtsgeschäftsarten.
          </Typography>
        </Box>
        <Tooltip title='Kennzahlen neu laden'>
          <span>
            <IconButton
              onClick={() => refetchArnoOverview()}
              disabled={isFetchingArnoOverview}
            >
              {isFetchingArnoOverview ? (
                <CircularProgress size={20} />
              ) : (
                <Refresh />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/*
        Importstand und Migrationskennzahlen gehören zur Migration, nicht zum Schreiben eines
        Baums. Über der Baumliste standen vorher »3239 Zuordnungen offen« und »76 Verweise ohne
        Ziel« als Erstes — für die Person, die eine Vorlage bearbeitet, ist das nicht die erste
        Frage (Sichtprüfung Teil 40). Ein Fehlerzustand des Modells bleibt überall sichtbar.
      */}
      {(view !== 'entscheidungsbaeume' || state.severity !== 'success') && (
        <Alert severity={state.severity} sx={{ mb: 2 }}>
          {state.text}
        </Alert>
      )}

      <Stack
        direction='row'
        spacing={2}
        sx={{
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
          display: view === 'entscheidungsbaeume' ? 'none' : 'flex',
        }}
      >
        {figures.map(figure => (
          <Card key={figure.label} variant='outlined' sx={{ minWidth: 220 }}>
            <CardContent>
              <Typography variant='h5'>
                {isLoadingArnoOverview ? '…' : figure.value}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                {figure.label}
              </Typography>
              {figure.hint && (
                <Typography variant='caption' color='text.secondary'>
                  {figure.hint}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Tabs
        value={view}
        onChange={(_event, next: View) => navigate(`/admin/arnotop/${next}`)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {VIEWS.map(item => (
          <Tab key={item} value={item} label={TAB_LABELS[item]} />
        ))}
      </Tabs>

      {view === 'entscheidungsbaeume' && <DecisionTemplateTab />}
      {view === 'zuordnungen' && <MappingTab />}
      {view === 'verweise' && <UnresolvedReferenceTab />}
      {view === 'importe' && <ImportRunTab />}
    </Box>
  );
};
